import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { portfolioAPI, propertyAPI } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt = (n, d = 0) => n == null ? '—' : `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const fmtPct = (n) => n == null ? '—' : `${Number(n).toFixed(1)}%`;
const fmtNum = (n) => n == null ? '—' : Number(n).toLocaleString('en-AU');
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// ── Mini bar (used in multiple places) ────────────────────────────────────────
function Bar({ pct, color = '#2d6a2d', height = 8, bg = '#eee', radius = 4 }) {
  const w = Math.max(0, Math.min(100, pct || 0));
  return (
    <div style={{ background: bg, borderRadius: radius, height, overflow: 'hidden' }}>
      <div style={{ width: `${w}%`, height: '100%', background: color, borderRadius: radius, transition: 'width 0.6s ease' }} />
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KPICard({ icon, label, value, sub, color = '#2d6a2d', trend }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '20px 22px',
      border: '1px solid #eee', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: 22 }}>{icon}</div>
        {trend != null && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 10,
            background: trend >= 0 ? '#e8f4e8' : '#fff0f0',
            color: trend >= 0 ? '#2d6a2d' : '#c0392b',
          }}>
            {trend >= 0 ? '▲' : '▼'} {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 12 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, marginTop: 4, letterSpacing: '-0.5px' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Section Header ────────────────────────────────────────────────────────────
function SectionHeader({ title, sub, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, marginTop: 32 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h2>
        {sub && <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>{sub}</div>}
      </div>
      {action}
    </div>
  );
}

// ── Monthly Cashflow Chart (CSS bar chart) ────────────────────────────────────
function MonthlyCashflowChart({ months }) {
  if (!months || months.length === 0) return null;
  const maxVal = Math.max(...months.map(m => Math.max(m.income, m.expenses, 1)));

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: '22px 24px', border: '1px solid #eee', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 20 }}>Monthly Cashflow — Current FY</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120, overflowX: 'auto', paddingBottom: 4 }}>
        {months.map(m => (
          <div key={m.month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 40, flex: '0 0 40px' }}>
            {/* Income bar */}
            <div style={{ width: 14, background: '#2d6a2d', borderRadius: '3px 3px 0 0', transition: 'height 0.5s', height: Math.round((m.income / maxVal) * 100) }} title={`Income: ${fmt(m.income)}`} />
            {/* Expense bar */}
            <div style={{ width: 14, background: '#e57373', borderRadius: '3px 3px 0 0', transition: 'height 0.5s', height: Math.round((m.expenses / maxVal) * 100) }} title={`Expenses: ${fmt(m.expenses)}`} />
            <div style={{ fontSize: 9, color: '#bbb', marginTop: 4, textAlign: 'center', lineHeight: 1.2 }}>{m.label.split(' ')[0]}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#555' }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#2d6a2d' }} /> Income
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#555' }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#e57373' }} /> Expenses
        </div>
      </div>
    </div>
  );
}

// ── Equity Donut (CSS ring) ───────────────────────────────────────────────────
function EquityRing({ equity, mortgage, size = 120 }) {
  const total = (equity || 0) + (mortgage || 0);
  const pct = total > 0 ? Math.round((equity / total) * 100) : 0;
  const r = 42, circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f0f0f0" strokeWidth="14" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#2d6a2d" strokeWidth="14"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#2d6a2d' }}>{pct}%</div>
        <div style={{ fontSize: 9, color: '#aaa', fontWeight: 600 }}>EQUITY</div>
      </div>
    </div>
  );
}

// ── Property Valuation Edit Modal ─────────────────────────────────────────────
function ValuationModal({ property, onClose, onSaved }) {
  const [form, setForm] = useState({
    market_value: property.market_value || '',
    mortgage_balance: property.mortgage_balance || '',
    mortgage_rate: property.mortgage_rate || '',
    mortgage_monthly: property.mortgage_monthly || '',
    purchase_price: property.purchase_price || '',
    purchase_date: property.purchase_date || '',
    rental_income_pw: property.rental_income_pw || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        payload[k] = v === '' ? null : (k === 'purchase_date' ? v : Number(v));
      });
      await propertyAPI.update(property.id, payload);
      onSaved();
    } catch {}
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>Update Valuation</h3>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{property.address_line1}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[
              ['market_value', 'Market Value ($)', 'number'],
              ['mortgage_balance', 'Mortgage Balance ($)', 'number'],
              ['mortgage_rate', 'Mortgage Rate (% pa)', 'number'],
              ['mortgage_monthly', 'Monthly Repayment ($)', 'number'],
              ['purchase_price', 'Purchase Price ($)', 'number'],
              ['purchase_date', 'Purchase Date', 'date'],
              ['rental_income_pw', 'Rental Income ($/wk)', 'number'],
            ].map(([key, label, type]) => (
              <div key={key} style={{ gridColumn: key === 'purchase_date' || key === 'rental_income_pw' ? 'auto' : 'auto' }}>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 5 }}>{label}</div>
                <input
                  type={type}
                  style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  value={form[key]}
                  onChange={e => set(key, e.target.value)}
                  step={type === 'number' ? 'any' : undefined}
                  min={type === 'number' ? 0 : undefined}
                />
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Property Row in portfolio table ──────────────────────────────────────────
function PropertyRow({ prop, onEdit, onNavigate, onCalculate }) {
  const hasValuation = prop.market_value != null;
  const lvrColor = !prop.lvr ? '#aaa' : prop.lvr > 80 ? '#c0392b' : prop.lvr > 60 ? '#e67e22' : '#2d6a2d';

  return (
    <tr style={{ borderBottom: '1px solid #f5f5f5', transition: 'background 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.background = '#fafff8'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <td style={{ padding: '14px 16px', verticalAlign: 'top' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {prop.cover_image_url ? (
            <img src={`${API_BASE}${prop.cover_image_url}`} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{ width: 40, height: 40, borderRadius: 8, background: '#e8f4e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🏠</div>
          )}
          <div>
            <button onClick={() => onNavigate(prop.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#1a1a1a', textAlign: 'left' }}>
              {prop.address_line1}
            </button>
            <div style={{ fontSize: 11, color: '#888' }}>{prop.city}, {prop.state} · {prop.property_type}</div>
            {(prop.bedrooms || prop.bathrooms) && (
              <div style={{ fontSize: 11, color: '#aaa' }}>
                {prop.bedrooms ? `${prop.bedrooms} bed` : ''} {prop.bathrooms ? `${prop.bathrooms} bath` : ''}
              </div>
            )}
          </div>
        </div>
      </td>
      <td style={tdStyle}>
        {hasValuation ? (
          <>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{fmt(prop.market_value)}</div>
            {prop.purchase_price && <div style={{ fontSize: 11, color: '#888' }}>Bought {fmt(prop.purchase_price)}</div>}
            {prop.capital_gain != null && (
              <div style={{ fontSize: 11, color: prop.capital_gain >= 0 ? '#2d6a2d' : '#c0392b', fontWeight: 600 }}>
                {prop.capital_gain >= 0 ? '▲' : '▼'} {fmt(Math.abs(prop.capital_gain))} ({fmtPct(prop.capital_gain_pct)})
              </div>
            )}
          </>
        ) : <span style={{ color: '#ccc', fontSize: 12 }}>Not set</span>}
      </td>
      <td style={tdStyle}>
        {prop.mortgage_balance != null ? (
          <>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{fmt(prop.mortgage_balance)}</div>
            {prop.mortgage_rate && <div style={{ fontSize: 11, color: '#888' }}>{fmtPct(prop.mortgage_rate)} pa</div>}
            {prop.mortgage_monthly && <div style={{ fontSize: 11, color: '#888' }}>{fmt(prop.mortgage_monthly)}/mo</div>}
          </>
        ) : <span style={{ color: '#ccc', fontSize: 12 }}>—</span>}
      </td>
      <td style={tdStyle}>
        {prop.equity != null ? (
          <>
            <div style={{ fontWeight: 700, fontSize: 14, color: prop.equity >= 0 ? '#2d6a2d' : '#c0392b' }}>{fmt(prop.equity)}</div>
            {prop.lvr != null && (
              <div style={{ marginTop: 6 }}>
                <Bar pct={prop.lvr} color={lvrColor} height={5} />
                <div style={{ fontSize: 10, color: lvrColor, fontWeight: 600, marginTop: 2 }}>LVR {fmtPct(prop.lvr)}</div>
              </div>
            )}
          </>
        ) : <span style={{ color: '#ccc', fontSize: 12 }}>—</span>}
      </td>
      <td style={tdStyle}>
        {prop.gross_yield != null ? (
          <>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1a5baa' }}>{fmtPct(prop.gross_yield)}</div>
            {prop.rental_income_pw && <div style={{ fontSize: 11, color: '#888' }}>{fmt(prop.rental_income_pw)}/wk</div>}
            {prop.annual_rental && <div style={{ fontSize: 11, color: '#888' }}>{fmt(prop.annual_rental)}/yr</div>}
          </>
        ) : <span style={{ color: '#ccc', fontSize: 12 }}>—</span>}
      </td>
      <td style={tdStyle}>
        <div style={{ fontSize: 12 }}>
          <div style={{ color: '#2d6a2d', fontWeight: 600 }}>▲ {fmt(prop.fy_income)}</div>
          <div style={{ color: '#e57373', fontWeight: 600 }}>▼ {fmt(prop.fy_expenses)}</div>
          <div style={{ color: prop.fy_net >= 0 ? '#2d6a2d' : '#c0392b', fontWeight: 700, marginTop: 3, fontSize: 13 }}>
            Net {fmt(prop.fy_net)}
          </div>
        </div>
      </td>
      <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn ghost sm" onClick={() => onEdit(prop)}>Edit Valuation</button>
          <button className="btn ghost sm" onClick={() => onCalculate(prop.id)}
            style={{ color: '#1f3a2e', borderColor: 'rgba(31,58,46,0.3)' }}>
            Calculator
          </button>
        </div>
      </td>
    </tr>
  );
}

const tdStyle = { padding: '14px 16px', verticalAlign: 'top' };

// ── Forecast Card ─────────────────────────────────────────────────────────────
function ForecastCard({ scenario, currentEquity }) {
  const { label, color, value_1yr, value_3yr, value_5yr, equity_1yr, equity_3yr, equity_5yr } = scenario;
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '20px 22px',
      border: `2px solid ${color}22`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>{label}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {[['1 Year', value_1yr, equity_1yr], ['3 Years', value_3yr, equity_3yr], ['5 Years', value_5yr, equity_5yr]].map(([period, val, eq]) => (
          <div key={period} style={{ background: `${color}08`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase' }}>{period}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color, marginTop: 6 }}>{fmt(val)}</div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>Portfolio Value</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#2d6a2d', marginTop: 6 }}>{fmt(eq)}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Equity</div>
            {currentEquity > 0 && eq > currentEquity && (
              <div style={{ fontSize: 10, color, fontWeight: 600, marginTop: 4 }}>
                +{fmt(eq - currentEquity)} gain
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── FY Summary Section ────────────────────────────────────────────────────────
function FYSummaryPanel({ totals, fyLabel }) {
  const { fy_income, fy_expenses, fy_net, fy_gst } = totals;
  const maxVal = Math.max(fy_income, fy_expenses, 1);
  const incPct = (fy_income / maxVal) * 100;
  const expPct = (fy_expenses / maxVal) * 100;

  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '22px 24px',
      border: '1px solid #eee', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 20 }}>
        Income vs Expenses — {fyLabel}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 12, color: '#2d6a2d', fontWeight: 600 }}>▲ Total Income</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#2d6a2d' }}>{fmt(fy_income)}</div>
          </div>
          <Bar pct={incPct} color="#2d6a2d" height={10} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, marginBottom: 6 }}>
            <div style={{ fontSize: 12, color: '#e57373', fontWeight: 600 }}>▼ Total Expenses</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#e57373' }}>{fmt(fy_expenses)}</div>
          </div>
          <Bar pct={expPct} color="#e57373" height={10} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ background: fy_net >= 0 ? '#e8f4e8' : '#fff0f0', borderRadius: 10, padding: '16px 20px' }}>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>NET CASHFLOW</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: fy_net >= 0 ? '#2d6a2d' : '#c0392b', marginTop: 4 }}>
              {fy_net >= 0 ? '+' : ''}{fmt(fy_net)}
            </div>
          </div>
          {fy_gst > 0 && (
            <div style={{ background: '#f8f8f8', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>TOTAL GST (expenses)</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#666', marginTop: 2 }}>{fmt(fy_gst)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Category Breakdown ────────────────────────────────────────────────────────
function CategoryPill({ label, amount, pct, color }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: '#555' }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>{fmt(amount)}</span>
      </div>
      <Bar pct={pct} color={color} height={6} />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PortfolioPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editProp, setEditProp] = useState(null);
  const [growthRate, setGrowthRate] = useState(6);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await portfolioAPI.getSummary();
      setData(r.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ display: 'grid', placeItems: 'center', height: '60vh', color: '#888' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
        <div>Building your portfolio view…</div>
      </div>
    </div>
  );

  if (!data || data.property_count === 0) return (
    <div style={{ padding: '40px 32px', maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🏘️</div>
      <h2 style={{ fontWeight: 800 }}>No Properties in Portfolio</h2>
      <p style={{ color: '#666' }}>Add properties to your account and set their market values to see your portfolio dashboard.</p>
      <button className="btn primary" onClick={() => navigate('/dashboard')}>Go to Properties</button>
    </div>
  );

  const { totals, forecast, properties, monthly_cashflow, fy_label } = data;
  const totalMV = totals.total_market_value || 0;
  const totalMortgage = totals.total_mortgage || 0;
  const totalEquity = totals.total_equity || 0;

  // Custom forecast at user-selected rate
  const customForecast = (rate) => ({
    value_1yr: totalMV * (1 + rate / 100),
    value_3yr: totalMV * ((1 + rate / 100) ** 3),
    value_5yr: totalMV * ((1 + rate / 100) ** 5),
    equity_1yr: totalMV * (1 + rate / 100) - totalMortgage,
    equity_3yr: totalMV * ((1 + rate / 100) ** 3) - totalMortgage,
    equity_5yr: totalMV * ((1 + rate / 100) ** 5) - totalMortgage,
  });

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>Portfolio Dashboard</h1>
          <p style={{ margin: '6px 0 0', color: '#666', fontSize: 14 }}>
            {data.property_count} {data.property_count === 1 ? 'property' : 'properties'} · {fy_label}
          </p>
        </div>
        <button className="btn ghost" onClick={load}>↻ Refresh</button>
      </div>

      {/* ── Top KPI row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 8 }}>
        <KPICard icon="🏘️" label="Total Market Value" value={fmt(totalMV)}
          sub={`${data.property_count} properties`} color="#1a1a1a" />
        <KPICard icon="🏦" label="Total Mortgage" value={fmt(totalMortgage)}
          sub={totals.avg_lvr != null ? `LVR ${fmtPct(totals.avg_lvr)}` : 'No mortgage data'} color="#e57373" />
        <KPICard icon="💚" label="Total Equity" value={fmt(totalEquity)}
          sub={totalMV > 0 ? `${Math.round(totalEquity / totalMV * 100)}% of portfolio` : ''} color="#2d6a2d" />
        <KPICard icon="📈" label="Capital Gain" value={fmt(totals.total_capital_gain)}
          sub={totals.total_purchase_price > 0 ? `Bought ${fmt(totals.total_purchase_price)}` : ''} color="#1a5baa" />
        <KPICard icon="🏷️" label="Portfolio Yield" value={fmtPct(totals.portfolio_yield)}
          sub={totals.total_annual_rental > 0 ? `${fmt(totals.total_annual_rental)}/yr rental` : 'No rental data'} color="#7b4fc9" />
        <KPICard icon="💰" label={`${fy_label} Net`} value={fmt(totals.fy_net)}
          sub={`Inc ${fmt(totals.fy_income)} · Exp ${fmt(totals.fy_expenses)}`}
          color={totals.fy_net >= 0 ? '#2d6a2d' : '#c0392b'} />
      </div>

      {/* ── Equity ring + LVR summary ── */}
      {totalMV > 0 && (
        <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', border: '1px solid #eee', marginBottom: 0, marginTop: 16, display: 'flex', alignItems: 'center', gap: 28 }}>
          <EquityRing equity={totalEquity} mortgage={totalMortgage} size={130} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 12 }}>Equity vs Debt</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>EQUITY</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#2d6a2d' }}>{fmt(totalEquity)}</div>
                <Bar pct={totalMV > 0 ? totalEquity / totalMV * 100 : 0} color="#2d6a2d" height={6} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>MORTGAGE</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#e57373' }}>{fmt(totalMortgage)}</div>
                <Bar pct={totalMV > 0 ? totalMortgage / totalMV * 100 : 0} color="#e57373" height={6} />
              </div>
              {totals.total_annual_mortgage_cost > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>ANNUAL REPAYMENTS</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#666' }}>{fmt(totals.total_annual_mortgage_cost)}</div>
                  <div style={{ fontSize: 11, color: '#aaa' }}>{fmt(totals.total_annual_mortgage_cost / 12)}/mo</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── FY Financials row ── */}
      <SectionHeader title="Financial Year Summary" sub={fy_label} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <FYSummaryPanel totals={totals} fyLabel={fy_label} />
        <MonthlyCashflowChart months={monthly_cashflow} />
      </div>

      {/* ── Property table ── */}
      <SectionHeader
        title="Property Breakdown"
        sub="Click a property to open its Redbook"
        action={
          <button className="btn ghost sm" onClick={() => navigate('/dashboard')}>
            + Add Property
          </button>
        }
      />
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9faf9', borderBottom: '2px solid #f0f0f0' }}>
              {['Property', 'Market Value', 'Mortgage', 'Equity / LVR', 'Yield', `${fy_label} Cashflow`, ''].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {properties.map(p => (
              <PropertyRow
                key={p.id}
                prop={p}
                onEdit={setEditProp}
                onNavigate={(id) => navigate(`/properties/${id}`)}
                onCalculate={(id) => navigate(`/properties/${id}?tab=calculator`)}
              />
            ))}
          </tbody>
          {properties.length > 1 && (
            <tfoot>
              <tr style={{ background: '#f9faf9', borderTop: '2px solid #f0f0f0' }}>
                <td style={{ padding: '12px 16px', fontWeight: 700, fontSize: 13 }}>Portfolio Total</td>
                <td style={{ ...tdStyle, fontWeight: 800, fontSize: 14 }}>{fmt(totals.total_market_value)}</td>
                <td style={{ ...tdStyle, fontWeight: 800, fontSize: 14, color: '#e57373' }}>{fmt(totals.total_mortgage)}</td>
                <td style={{ ...tdStyle, fontWeight: 800, fontSize: 14, color: '#2d6a2d' }}>{fmt(totals.total_equity)}</td>
                <td style={{ ...tdStyle, fontWeight: 700, fontSize: 13, color: '#1a5baa' }}>{fmtPct(totals.portfolio_yield)}</td>
                <td style={tdStyle}>
                  <div style={{ fontSize: 12 }}>
                    <div style={{ color: '#2d6a2d', fontWeight: 600 }}>▲ {fmt(totals.fy_income)}</div>
                    <div style={{ color: '#e57373', fontWeight: 600 }}>▼ {fmt(totals.fy_expenses)}</div>
                    <div style={{ color: totals.fy_net >= 0 ? '#2d6a2d' : '#c0392b', fontWeight: 800, fontSize: 14, marginTop: 3 }}>
                      Net {fmt(totals.fy_net)}
                    </div>
                  </div>
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
        {properties.every(p => !p.market_value) && (
          <div style={{ padding: '20px 24px', color: '#888', fontSize: 13, textAlign: 'center', borderTop: '1px solid #f0f0f0' }}>
            Set market values via "Edit Valuation" to unlock equity, LVR and yield calculations.
          </div>
        )}
      </div>

      {/* ── Market Forecast ── */}
      {totalMV > 0 && (
        <>
          <SectionHeader
            title="Market Value Forecast"
            sub="Based on projected annual growth rates — for illustration purposes"
            action={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: '#888' }}>Custom rate</span>
                <input
                  type="range" min="1" max="20" step="0.5" value={growthRate}
                  onChange={e => setGrowthRate(Number(e.target.value))}
                  style={{ width: 100 }}
                />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1a5baa', minWidth: 36 }}>{growthRate}%</span>
              </div>
            }
          />

          {/* Built-in 3 scenarios */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
            {Object.values(forecast).map(scenario => (
              <ForecastCard key={scenario.label} scenario={scenario} currentEquity={totalEquity} />
            ))}
          </div>

          {/* Custom rate card */}
          <ForecastCard
            scenario={{
              label: `Custom Rate (${growthRate}%/yr)`,
              color: '#7b4fc9',
              ...customForecast(growthRate),
            }}
            currentEquity={totalEquity}
          />

          <div style={{ marginTop: 12, padding: '10px 16px', background: '#fffbf0', borderRadius: 8, fontSize: 12, color: '#888', border: '1px solid #f0e8c0' }}>
            ⚠️ Forecast figures are illustrative only and assume constant annual growth with no change to mortgage balance. Not financial advice.
          </div>
        </>
      )}

      {/* Valuation edit modal */}
      {editProp && (
        <ValuationModal
          property={editProp}
          onClose={() => setEditProp(null)}
          onSaved={() => { setEditProp(null); load(); }}
        />
      )}
    </div>
  );
}
